import { ParsedVendor } from './vendorData';
import { apiGet, apiPut, apiPatch, planningPath } from '../services/PlanningApiClient';

interface ApiVendorRow {
  id: string;
  vendor_id: string;
  name: string;
  category: string | null;
  categories: string[];
  services: string[];
  description: string | null;
  instagram: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  marketplace: string | null;
  price_range: string;
  rating: number;
  review_count: number;
  verified: boolean;
  featured: boolean;
  team_tag: string | null;
  availability: string[];
  specializations: string[];
  notes: string | null;
  status: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  whatsapp: string | null;
  contact_channels: string[] | null;
  travel_fee: string | null;
  consultation_fee: string | null;
  base_location: string | null;
  // Intelligence fields
  sentiment: string | null;
  sentiment_note: string | null;
  signal_source: string | null;
  tags: string[] | null;
  rate_card_notes: string | null;
  rate_card_valid_until: string | null;
  // Planner shortlist assessment
  shortlist_rank: number | null;
  shortlist_tier: string | null;
  best_fit: string | null;
}

function parsePressFeatureCount(description: string | null): number {
  if (!description) return 0;
  const m = description.match(/Featured in (\d+) BellaNaija/i);
  return m ? parseInt(m[1], 10) : 0;
}

// Rejects rows where the Instagram handle is clearly not a real account:
// - contains spaces (e.g. "Victoria Island")
// - is a bare single common word under 6 chars (e.g. "Lagos", "Ikoyi")
// - contains only a trailing punctuation char (e.g. "Lagos.")
// Real vendor handles are alphanumeric with dots/underscores, 3-30 chars.
function isValidVendorHandle(instagram: string | null, name: string): boolean {
  const handle = (instagram ?? '').replace(/^https?:\/\/www\.instagram\.com\//i, '').replace(/\/$/, '');
  if (!handle || handle.includes(' ')) return false;
  if (handle.length < 3) return false;
  // Single bare word with no digits, dots or underscores — likely a place name
  if (/^[a-zA-Z]{1,5}[.]*$/.test(handle)) return false;
  return true;
}

// ── Vendor cache ──────────────────────────────────────────────────────────────

/** In-memory cache — source of truth for the session after first API fetch */
let vendorsMemoryCache: ParsedVendor[] | null = null;
/** Deduplicates concurrent in-flight API calls */
let vendorsFetchPromise: Promise<ParsedVendor[]> | null = null;

const toStringArray = (arr: unknown): string[] =>
  Array.isArray(arr)
    ? arr.map(s => (typeof s === 'string' ? s : (s as any)?.name ?? String(s ?? '')))
    : [];

function mapApiRow(row: ApiVendorRow): ParsedVendor {
  return {
    id: row.id.toString(),
    apiKey: row.vendor_id,
    name: row.name,
    category: row.category ?? undefined,
    services: toStringArray(row.services),
    description: row.description ?? '',
    contact: {
      instagram: row.instagram ? `https://www.instagram.com/${row.instagram}/` : '',
      website: row.website ?? undefined,
      phone: row.phone ?? undefined,
      email: row.email ?? undefined,
      address: row.address ?? undefined,
    },
    rating: typeof row.rating === 'number' ? row.rating : 0,
    reviewCount: row.review_count,
    priceRange: (row.price_range as ParsedVendor['priceRange']) ?? '₦₦',
    marketplace: row.marketplace ?? null,
    availability: toStringArray(row.availability),
    specializations: toStringArray(row.specializations),
    images: [],
    verified: row.verified,
    featured: row.featured,
    createdAt: new Date(row.created_at),
    sourceEvents: [],
    instagramHandle: row.instagram ?? '',
    teamTag: row.team_tag ?? undefined,
    pressFeatureCount: parsePressFeatureCount(row.description),
    whatsapp: row.whatsapp ?? undefined,
    contactChannels: row.contact_channels ?? undefined,
    travelFee: row.travel_fee ?? undefined,
    consultationFee: row.consultation_fee ?? undefined,
    baseLocation: row.base_location ?? undefined,
    // Intelligence
    sentiment: (row.sentiment as ParsedVendor['sentiment']) ?? 'undecided',
    sentimentNote: row.sentiment_note ?? null,
    signalSource: (row.signal_source as ParsedVendor['signalSource']) ?? 'scraped',
    tags: row.tags ?? [],
    notes: row.notes ?? null,
    archived: row.archived ?? false,
    rateCardNotes: row.rate_card_notes ?? null,
    rateCardValidUntil: row.rate_card_valid_until ? row.rate_card_valid_until.slice(0, 10) : null,
    // Shortlist assessment — only ~32 vendors carry one; keep null otherwise
    // so the UI can tell "unranked" apart from rank 0.
    shortlistRank: typeof row.shortlist_rank === 'number' ? row.shortlist_rank : null,
    shortlistTier: row.shortlist_tier ?? null,
    bestFit: row.best_fit ?? null,
  };
}

async function fetchVendorsFromApi(): Promise<ParsedVendor[]> {
  const response = await apiGet<{ vendors: ApiVendorRow[] }>(planningPath('/vendors'));
  const vendors = (response.vendors || [])
    // The handle check exists to drop bad scrape data — rows where the
    // "instagram" field actually holds a place name ("Lagos"). It rejected a
    // null handle too, which hid every scraped vendor that simply has no
    // Instagram: 47 of them, 23 with quotes against them and 3 with
    // bookings. A missing handle says nothing about whether a vendor is
    // real, so only reject one that is present and bogus.
    .filter(row => row.signal_source === 'manual' || !row.instagram || isValidVendorHandle(row.instagram, row.name))
    .map(mapApiRow);
  vendorsMemoryCache = vendors;
  vendorsFetchPromise = null;
  window.dispatchEvent(new CustomEvent('anojeje:vendors-refreshed', { detail: vendors }));
  return vendors;
}

/**
 * Archived vendors, fetched on demand.
 *
 * The Vendors page filtered its loaded list by `archived === true`, but that
 * list comes from /vendors, which defaults to archived=false — so the
 * Archived view could only ever be empty, however many archived vendors
 * existed. Deliberately uncached: it's opened rarely and must reflect an
 * archive/restore that just happened.
 */
export async function loadArchivedVendors(): Promise<ParsedVendor[]> {
  try {
    const response = await apiGet<{ vendors: ApiVendorRow[] }>(
      planningPath('/vendors?archived=true')
    );
    return (response.vendors || []).map(mapApiRow);
  } catch (error) {
    console.error('Error loading archived vendors from API:', error);
    return [];
  }
}

export async function loadProjectVendors(): Promise<ParsedVendor[]> {
  // Memory cache — same session, instant
  if (vendorsMemoryCache) return vendorsMemoryCache;

  // First load — deduplicate concurrent calls then await
  if (!vendorsFetchPromise) {
    vendorsFetchPromise = fetchVendorsFromApi().catch(() => []);
  }
  return vendorsFetchPromise;
}

// Load all vendor data from the primary source (Postgres DB)
// Note: Scraped, integrated, and form submission vendors are now deprecated.
// Source of truth: DB via /planning/vendors endpoint
export async function loadVendorData(): Promise<ParsedVendor[]> {
  try {
    return await loadProjectVendors();
  } catch (error) {
    console.error('Error loading project vendors from API:', error);
    return [];
  }
}

export async function refreshVendorData(): Promise<ParsedVendor[]> {
  vendorsMemoryCache = null;
  vendorsFetchPromise = null;
  return fetchVendorsFromApi();
}

export async function updateVendorProfile(
  vendorId: string,
  vendor: {
    name: string;
    description: string;
    priceRange: string;
    marketplace?: string | null;
    contact: ParsedVendor['contact'];
    services: string[];
    specializations: string[];
    availability: string[];
    rating: number;
    // Shortlist assessment — all optional, all part of VENDOR_UPDATABLE on
    // the backend already; undefined fields are simply omitted from the
    // PUT body rather than sent as null and clobbering an existing value.
    sentiment?: string;
    sentimentNote?: string | null;
    shortlistRank?: number | null;
    shortlistTier?: string | null;
    bestFit?: string | null;
  },
): Promise<void> {
  await apiPut(planningPath(`/vendors/${vendorId}`), {
    name: vendor.name,
    description: vendor.description,
    price_range: vendor.priceRange || '₦₦',
    marketplace: vendor.marketplace || null,
    phone: vendor.contact.phone || null,
    email: vendor.contact.email || null,
    website: vendor.contact.website || null,
    address: vendor.contact.address || null,
    instagram: (vendor.contact.instagram || '').replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/^@/, '').replace(/\/$/, '') || null,
    services: vendor.services,
    specializations: vendor.specializations,
    availability: vendor.availability,
    rating: Number(vendor.rating) || 0,
    ...(vendor.sentiment !== undefined && { sentiment: vendor.sentiment }),
    ...(vendor.sentimentNote !== undefined && { sentiment_note: vendor.sentimentNote }),
    ...(vendor.shortlistRank !== undefined && { shortlist_rank: vendor.shortlistRank }),
    ...(vendor.shortlistTier !== undefined && { shortlist_tier: vendor.shortlistTier }),
    ...(vendor.bestFit !== undefined && { best_fit: vendor.bestFit }),
  });
  vendorsMemoryCache = null;
  vendorsFetchPromise = null;
}

export async function updateVendorRateCard(
  vendorId: string,
  updates: { rateCardNotes?: string | null; rateCardValidUntil?: string | null },
): Promise<void> {
  await apiPatch(planningPath(`/vendors/${vendorId}/rate-card`), {
    rate_card_notes: updates.rateCardNotes ?? null,
    rate_card_valid_until: updates.rateCardValidUntil ?? null,
  });
  vendorsMemoryCache = null;
}

// Get vendor statistics
export function getVendorStats(vendors: ParsedVendor[]) {
  const totalVendors = vendors.length;
  const verifiedVendors = vendors.filter(v => v.verified).length;
  const featuredVendors = vendors.filter(v => v.featured).length;
  
  const totalRating = vendors.reduce((sum, v) => sum + v.rating, 0);
  const avgRating = totalVendors > 0 ? (totalRating / totalVendors).toFixed(1) : '0.0';

  // Get most common price range
  const priceRangeCounts: { [key: string]: number } = {};
  vendors.forEach(vendor => {
    const range = vendor.priceRange;
    priceRangeCounts[range] = (priceRangeCounts[range] || 0) + 1;
  });

  const mostCommonPriceRange = Object.entries(priceRangeCounts)
    .sort((a, b) => b[1] - a[1])[0];
  
  const priceRangeStats = mostCommonPriceRange 
    ? `${mostCommonPriceRange[0]} 
    `
    : 'N/A';
  
  const priceRangeDetail = mostCommonPriceRange 
    ? `most common among ${totalVendors} vendors`
    : '';

  return {
    totalVendors,
    verifiedVendors,
    featuredVendors,
    avgRating,
    priceRangeStats,
    priceRangeDetail,
    lastScrapedAt: new Date().toISOString()
  };
}
